<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import CapacityField from './CapacityField.vue';

const props = defineProps({
  theater: {
    type: [String, Number],
    default: null,
  },
  classroom: {
    type: [String, Number],
    default: null,
  },
  standing: {
    type: [String, Number],
    default: null,
  },
  banquet: {
    type: [String, Number],
    default: null,
  },
  boardroom: {
    type: [String, Number],
    default: null,
  },
  uShape: {
    type: [String, Number],
    default: null,
  },
  concert: {
    type: [String, Number],
    default: null,
  },
  cocktail: {
    type: [String, Number],
    default: null,
  },
  maxCapacitySeated: {
    type: [String, Number],
    default: null,
  },
  maxCapacitySeatedFurn: {
    type: [String, Number],
    default: null,
  },
  maxCapacityStanding: {
    type: [String, Number],
    default: null,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const selectedStandingConfig = ref(null);
const selectedFurnConfig = ref(null);
const selectedFreeConfig = ref(null);
const activeEditKey = ref(null);

const FREE_LAYOUTS = ['theater'];
const FURN_LAYOUTS = ['classroom', 'banquet', 'boardroom', 'u_shape'];
const STAND_LAYOUTS = ['standing', 'concert', 'cocktail'];

const freeConfigurations = [
  {
    key: 'theater',
    label: 'SETTINGS.SPACES.THEATER',
    icon: 'i-lucide-theater',
  },
];

const furnConfigurations = [
  {
    key: 'classroom',
    label: 'SETTINGS.SPACES.CLASSROOM',
    icon: 'i-lucide-school',
  },
  {
    key: 'banquet',
    label: 'SETTINGS.SPACES.BANQUET',
    icon: 'i-lucide-utensils-crossed',
  },
  {
    key: 'boardroom',
    label: 'SETTINGS.SPACES.BOARDROOM',
    icon: 'i-lucide-armchair',
  },
  {
    key: 'u_shape',
    label: 'SETTINGS.SPACES.U_SHAPE',
    icon: 'i-lucide-layout-dashboard',
  },
];

const standingConfigurations = [
  {
    key: 'standing',
    label: 'SETTINGS.SPACES.STANDING',
    icon: 'i-lucide-music',
  },
  {
    key: 'concert',
    label: 'SETTINGS.SPACES.CONCERT',
    icon: 'i-lucide-spotlight',
  },
  {
    key: 'cocktail',
    label: 'SETTINGS.SPACES.COCKTAIL',
    icon: 'i-lucide-wine',
  },
];

const availableConfigurations = [
  ...standingConfigurations,
  ...furnConfigurations,
  ...freeConfigurations,
];

const activeConfigurations = computed(() => {
  const active = [];
  if (props.theater != null) active.push('theater');
  if (props.classroom != null) active.push('classroom');
  if (props.banquet != null) active.push('banquet');
  if (props.boardroom != null) active.push('boardroom');
  if (props.uShape != null) active.push('u_shape');
  if (props.standing != null) active.push('standing');
  if (props.concert != null) active.push('concert');
  if (props.cocktail != null) active.push('cocktail');
  return active;
});

const activeStandingLayouts = computed(() =>
  activeConfigurations.value.filter(key => STAND_LAYOUTS.includes(key))
);

const activeFurnLayouts = computed(() =>
  activeConfigurations.value.filter(key => FURN_LAYOUTS.includes(key))
);

const activeFreeLayouts = computed(() =>
  activeConfigurations.value.filter(key => FREE_LAYOUTS.includes(key))
);

const remainingForSection = layouts =>
  layouts
    .filter(config => !activeConfigurations.value.includes(config.key))
    .map(config => ({
      id: config.key,
      name: t(config.label),
      icon: config.icon,
    }));

const remainingStanding = computed(() =>
  remainingForSection(standingConfigurations)
);
const remainingFurn = computed(() => remainingForSection(furnConfigurations));
const remainingFree = computed(() => remainingForSection(freeConfigurations));

const getConfigValue = key => {
  const fieldMap = {
    theater: props.theater,
    classroom: props.classroom,
    banquet: props.banquet,
    boardroom: props.boardroom,
    u_shape: props.uShape,
    standing: props.standing,
    concert: props.concert,
    cocktail: props.cocktail,
  };
  return fieldMap[key];
};

const getConfigLabel = key => {
  const config = availableConfigurations.find(c => c.key === key);
  return config ? t(config.label) : '';
};

const getConfigIcon = key => {
  const config = availableConfigurations.find(c => c.key === key);
  return config?.icon ?? null;
};

const maxForLayout = key => {
  if (STAND_LAYOUTS.includes(key)) return Number(props.maxCapacityStanding) || 0;
  if (FREE_LAYOUTS.includes(key)) return Number(props.maxCapacitySeated) || 0;
  if (FURN_LAYOUTS.includes(key)) return Number(props.maxCapacitySeatedFurn) || 0;
  return 0;
};

const defaultCapacityFor = key => maxForLayout(key);

const updateCapacity = (field, value) => {
  emit('update', field, value);
};

const removeConfiguration = key => {
  emit('update', key, null);
};

const onSelectConfiguration = (selectedRef, newConfig) => {
  if (newConfig?.id) {
    emit('update', newConfig.id, defaultCapacityFor(newConfig.id));
    selectedRef.value = null;
  }
};

watch(selectedStandingConfig, newConfig => {
  onSelectConfiguration(selectedStandingConfig, newConfig);
});
watch(selectedFurnConfig, newConfig => {
  onSelectConfiguration(selectedFurnConfig, newConfig);
});
watch(selectedFreeConfig, newConfig => {
  onSelectConfiguration(selectedFreeConfig, newConfig);
});
</script>

<template>
  <div class="space-y-6 py-2">
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
      <!-- Standing -->
      <section
        class="space-y-2 rounded-xl border border-n-weak bg-n-alpha-1/40 p-3"
      >
        <h4
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10 px-1"
        >
          {{ t('SETTINGS.SPACES.SECTION_STANDING') }}
        </h4>
        <CapacityField
          field-key="max_capacity_standing"
          :active-edit-key="activeEditKey"
          :label="t('SETTINGS.SPACES.MAX_CAPACITY')"
          :value="maxCapacityStanding"
          allow-n-a
          @update="value => updateCapacity('max_capacity_standing', value)"
          @start-edit="activeEditKey = $event"
          @end-edit="activeEditKey = null"
        />
        <CapacityField
          v-for="configKey in activeStandingLayouts"
          :key="configKey"
          :field-key="configKey"
          :active-edit-key="activeEditKey"
          :label="getConfigLabel(configKey)"
          :icon="getConfigIcon(configKey)"
          :value="getConfigValue(configKey)"
          removable
          @update="value => updateCapacity(configKey, value)"
          @remove="removeConfiguration(configKey)"
          @start-edit="activeEditKey = $event"
          @end-edit="activeEditKey = null"
        />
        <SingleSelect
          v-if="remainingStanding.length"
          v-model="selectedStandingConfig"
          :options="remainingStanding"
          :placeholder="t('SETTINGS.SPACES.ADD_CONFIGURATION')"
          placeholder-icon="i-lucide-plus"
          :search-placeholder="t('SETTINGS.SPACES.SEARCH_CONFIGURATION')"
          :disable-search="remainingStanding.length < 5"
        />
      </section>

      <!-- Seating -->
      <section
        class="space-y-2 rounded-xl border border-n-weak bg-n-alpha-1/40 p-3"
      >
        <h4
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10 px-1"
        >
          {{ t('SETTINGS.SPACES.SECTION_SEATED_FREE') }}
        </h4>
        <CapacityField
          field-key="max_capacity_seated"
          :active-edit-key="activeEditKey"
          :label="t('SETTINGS.SPACES.MAX_CAPACITY')"
          :value="maxCapacitySeated"
          allow-n-a
          @update="value => updateCapacity('max_capacity_seated', value)"
          @start-edit="activeEditKey = $event"
          @end-edit="activeEditKey = null"
        />
        <CapacityField
          v-for="configKey in activeFreeLayouts"
          :key="configKey"
          :field-key="configKey"
          :active-edit-key="activeEditKey"
          :label="getConfigLabel(configKey)"
          :icon="getConfigIcon(configKey)"
          :value="getConfigValue(configKey)"
          removable
          @update="value => updateCapacity(configKey, value)"
          @remove="removeConfiguration(configKey)"
          @start-edit="activeEditKey = $event"
          @end-edit="activeEditKey = null"
        />
        <SingleSelect
          v-if="remainingFree.length"
          v-model="selectedFreeConfig"
          :options="remainingFree"
          :placeholder="t('SETTINGS.SPACES.ADD_CONFIGURATION')"
          placeholder-icon="i-lucide-plus"
          :search-placeholder="t('SETTINGS.SPACES.SEARCH_CONFIGURATION')"
          :disable-search="true"
        />
      </section>

      <!-- Table seating -->
      <section
        class="space-y-2 rounded-xl border border-n-weak bg-n-alpha-1/40 p-3"
      >
        <h4
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-10 px-1"
        >
          {{ t('SETTINGS.SPACES.SECTION_SEATED_FURN') }}
        </h4>
        <CapacityField
          field-key="max_capacity_seated_furn"
          :active-edit-key="activeEditKey"
          :label="t('SETTINGS.SPACES.MAX_CAPACITY')"
          :value="maxCapacitySeatedFurn"
          allow-n-a
          @update="value => updateCapacity('max_capacity_seated_furn', value)"
          @start-edit="activeEditKey = $event"
          @end-edit="activeEditKey = null"
        />
        <CapacityField
          v-for="configKey in activeFurnLayouts"
          :key="configKey"
          :field-key="configKey"
          :active-edit-key="activeEditKey"
          :label="getConfigLabel(configKey)"
          :icon="getConfigIcon(configKey)"
          :value="getConfigValue(configKey)"
          removable
          @update="value => updateCapacity(configKey, value)"
          @remove="removeConfiguration(configKey)"
          @start-edit="activeEditKey = $event"
          @end-edit="activeEditKey = null"
        />
        <SingleSelect
          v-if="remainingFurn.length"
          v-model="selectedFurnConfig"
          :options="remainingFurn"
          :placeholder="t('SETTINGS.SPACES.ADD_CONFIGURATION')"
          placeholder-icon="i-lucide-plus"
          :search-placeholder="t('SETTINGS.SPACES.SEARCH_CONFIGURATION')"
          :disable-search="remainingFurn.length < 5"
        />
      </section>
    </div>
  </div>
</template>
